import { useState, useEffect } from "react"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { Activity, Mail, Lock, MessageSquare, Loader2 } from "lucide-react"
import { Button } from "@/shared/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/components/ui/card"
import { Input } from "@/shared/components/ui/input"
import { Label } from "@/shared/components/ui/label"
import { authService } from "@/shared/services/auth"
import { useToast } from "@/shared/hooks/useToast"

/**
 * 重置密码页面
 * 使用验证码重置密码
 */
export default function ResetPassword() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { showError, showSuccess } = useToast()

  const emailFromQuery = searchParams.get('email') || ''
  
  const [formData, setFormData] = useState({
    email: emailFromQuery,
    code: "",
    newPassword: "",
    confirmPassword: "",
  })
  const [isLoading, setIsLoading] = useState(false)
  const [countdown, setCountdown] = useState(0)
  const [error, setError] = useState<string | null>(null)

  // 倒计时效果
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [countdown])

  // 检查密码强度
  const getPasswordStrength = (password: string) => {
    if (password.length === 0) return { strength: 0, message: '' }
    
    let strength = 0
    const checks = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
    }

    if (checks.length) strength++
    if (checks.uppercase) strength++
    if (checks.lowercase) strength++
    if (checks.number) strength++

    const missing = []
    if (!checks.length) missing.push('至少8位')
    if (!checks.uppercase) missing.push('大写字母')
    if (!checks.lowercase) missing.push('小写字母')
    if (!checks.number) missing.push('数字')

    return {
      strength,
      message: missing.length > 0 ? `缺少：${missing.join('、')}` : '密码强度符合要求',
      valid: strength === 4,
    }
  }

  const passwordStrength = getPasswordStrength(formData.newPassword)

  // 重新发送验证码
  const handleResendCode = async () => {
    if (!formData.email.trim()) {
      setError("请输入邮箱地址")
      return
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(formData.email)) {
      setError("请输入有效的邮箱地址")
      return
    }

    setIsLoading(true)
    setError(null)
    try {
      const result = await authService.forgotPassword(formData.email.trim())
      if (result.success) {
        showSuccess("验证码已重新发送")
        setCountdown(60)
      } else {
        setError(result.error || '发送验证码失败，请稍后重试')
        showError(result.error || '发送验证码失败，请稍后重试')
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '发送验证码失败，请稍后重试'
      setError(errorMessage)
      showError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    // 基础验证
    if (!formData.email.trim()) {
      setError("请输入邮箱地址")
      return
    }

    if (!formData.code.trim()) {
      setError("请输入验证码")
      return
    }

    if (formData.code.length !== 6) {
      setError("验证码为6位数字")
      return
    }

    if (!formData.newPassword.trim()) {
      setError("请输入新密码")
      return
    }

    if (!formData.confirmPassword.trim()) {
      setError("请确认新密码")
      return
    }

    // 邮箱格式验证
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(formData.email)) {
      setError("请输入有效的邮箱地址")
      return
    }

    // 密码强度验证
    if (formData.newPassword.length < 8) {
      setError("密码长度至少8位")
      return
    }

    if (!/[A-Z]/.test(formData.newPassword)) {
      setError("密码必须包含至少一个大写字母")
      return
    }

    if (!/[a-z]/.test(formData.newPassword)) {
      setError("密码必须包含至少一个小写字母")
      return
    }

    if (!/[0-9]/.test(formData.newPassword)) {
      setError("密码必须包含至少一个数字")
      return
    }

    // 密码确认验证
    if (formData.newPassword !== formData.confirmPassword) {
      setError("两次输入的密码不一致")
      return
    }

    setIsLoading(true)
    try {
      const result = await authService.resetPassword({
        email: formData.email.trim(),
        code: formData.code.trim(),
        new_password: formData.newPassword,
      })
      if (result.success) {
        showSuccess("密码重置成功，请使用新密码登录")
        setTimeout(() => {
          navigate("/login")
        }, 2000)
      } else {
        setError(result.error || '重置密码失败，请稍后重试')
        showError(result.error || '重置密码失败，请稍后重试')
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '重置密码失败，请稍后重试'
      setError(errorMessage)
      showError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo和标题 */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-2xl mb-4">
            <Activity className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight mb-2">重置密码</h1>
          <p className="text-slate-500 dark:text-slate-400">
            输入验证码和新密码
          </p>
        </div>

        {/* 重置密码卡片 */}
        <Card className="border-0 shadow-xl backdrop-blur-sm bg-white/80 dark:bg-slate-900/80">
          <CardHeader className="space-y-1 pb-6">
            <CardTitle className="text-2xl font-semibold">设置新密码</CardTitle>
            <CardDescription>
              验证码有效期为10分钟
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* 邮箱输入 */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium">
                  邮箱地址
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    value={formData.email}
                    onChange={(e) => {
                      setFormData({ ...formData, email: e.target.value })
                      setError(null)
                    }}
                    className="pl-10 h-11 rounded-lg border-slate-200 dark:border-slate-800 focus:border-primary focus:ring-2 focus:ring-primary/20"
                    disabled={isLoading}
                    autoComplete="email"
                    required
                  />
                </div>
              </div>

              {/* 验证码输入 */}
              <div className="space-y-2">
                <Label htmlFor="code" className="text-sm font-medium">
                  验证码
                </Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <MessageSquare className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="code"
                      type="text"
                      placeholder="6位验证码"
                      value={formData.code}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 6)
                        setFormData({ ...formData, code: value })
                        setError(null)
                      }}
                      className="pl-10 h-11 rounded-lg border-slate-200 dark:border-slate-800 focus:border-primary focus:ring-2 focus:ring-primary/20 text-center text-lg tracking-widest"
                      disabled={isLoading}
                      maxLength={6}
                      required
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleResendCode}
                    disabled={isLoading || countdown > 0}
                    className="h-11 px-4 whitespace-nowrap"
                  >
                    {isLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : countdown > 0 ? (
                      `${countdown}秒`
                    ) : (
                      '重新发送'
                    )}
                  </Button>
                </div>
              </div>

              {/* 新密码输入 */}
              <div className="space-y-2">
                <Label htmlFor="newPassword" className="text-sm font-medium">
                  新密码
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="newPassword"
                    type="password"
                    placeholder="至少8位，包含大小写字母和数字"
                    value={formData.newPassword}
                    onChange={(e) => {
                      setFormData({ ...formData, newPassword: e.target.value })
                      setError(null)
                    }}
                    className="pl-10 h-11 rounded-lg border-slate-200 dark:border-slate-800 focus:border-primary focus:ring-2 focus:ring-primary/20"
                    disabled={isLoading}
                    autoComplete="new-password"
                    required
                  />
                </div>
                {/* 密码强度提示 */}
                {formData.newPassword.length > 0 && (
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden">
                        <div
                          className={`h-full transition-all ${
                            passwordStrength.strength === 4
                              ? 'bg-green-500'
                              : passwordStrength.strength >= 2
                              ? 'bg-yellow-500'
                              : 'bg-red-500'
                          }`}
                          style={{ width: `${(passwordStrength.strength / 4) * 100}%` }}
                        />
                      </div>
                      <span
                        className={`text-xs ${
                          passwordStrength.valid
                            ? 'text-green-600 dark:text-green-400'
                            : 'text-slate-500 dark:text-slate-400'
                        }`}
                      >
                        {passwordStrength.strength}/4
                      </span>
                    </div>
                    <p
                      className={`text-xs ${
                        passwordStrength.valid
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-slate-500 dark:text-slate-400'
                      }`}
                    >
                      {passwordStrength.message}
                    </p>
                  </div>
                )}
                {formData.newPassword.length === 0 && (
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    密码需至少8位，包含大写字母、小写字母和数字
                  </p>
                )}
              </div>

              {/* 确认密码输入 */}
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-sm font-medium">
                  确认密码
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="再次输入新密码"
                    value={formData.confirmPassword}
                    onChange={(e) => {
                      setFormData({ ...formData, confirmPassword: e.target.value })
                      setError(null)
                    }}
                    className="pl-10 h-11 rounded-lg border-slate-200 dark:border-slate-800 focus:border-primary focus:ring-2 focus:ring-primary/20"
                    disabled={isLoading}
                    autoComplete="new-password"
                    required
                  />
                </div>
              </div>

              {/* 错误提示 */}
              {error && (
                <div className="rounded-lg bg-destructive/10 border border-destructive/20 p-3">
                  <p className="text-sm text-destructive">{error}</p>
                </div>
              )}

              {/* 提交按钮 */}
              <Button
                type="submit"
                className="w-full h-11 rounded-lg text-base font-medium shadow-sm hover:shadow-md transition-all"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    重置中...
                  </>
                ) : (
                  "重置密码"
                )}
              </Button>
            </form>

            {/* 返回登录链接 */}
            <div className="mt-6 text-center">
              <Link
                to="/login"
                className="text-sm text-primary font-medium hover:underline"
              >
                返回登录
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

